// Display cleanup for assistant messages: the model emits its suggestion as a
// fenced ```json block (parsed separately to drive the Apply button), but that
// raw JSON shouldn't appear in the chat bubble — show only the conversation.

/**
 * Marks where a suggestion block was removed, so the pass below can tell a
 * sentence that introduced the removed block from ordinary prose.
 *
 * ⚠️ A NUL is used because it cannot occur in model prose. Any visible sentinel
 * risks matching real text and deleting a line the user wrote or read.
 */
const REMOVED = '\u0000';

/**
 * ⚠️⚠️ REMOVING A BLOCK ORPHANS THE SENTENCE THAT INTRODUCED IT. The model
 * writes "Here's a starter set:" and then the ```json``` the Apply button is
 * built from — so stripping the block left the colon dangling over nothing, and
 * the reply read as truncated mid-thought:
 *
 *     Want me to turn some of these into actual tasks? Here's a starter set:
 *
 *     And if you want this to stick as an ongoing practice:
 *
 * The content is not missing — it is in the Apply button — but the prose no
 * longer says so.
 *
 * ⚠️ CONSERVATIVE BY DESIGN. A line is dropped only when it ends in a colon AND
 * the next thing was a removed block. "My suggestion for today's top 3, in
 * order:" followed by a real list is untouched, because what follows it was
 * never removed.
 *
 * ⚠️ LIST ITEMS ARE NEVER DROPPED, even when they end in a colon — deleting one
 * would leave a hole in the middle of a list, which is a worse artifact than
 * the dangling colon this fixes.
 */
const isListItem = (l: string) => /^\s*([-*+]|\d+[.)])\s/.test(l);

/**
 * Strip a trailing "…and here it is:" clause, keeping any complete sentence in
 * front of it.
 *
 * ⚠️⚠️ CLAUSE-LEVEL, NOT LINE-LEVEL. The real reply put a genuine question and
 * the lead-in on ONE line:
 *
 *     Want me to turn some of these into actual tasks? Here's a starter set:
 *
 * Dropping the line would have taken the question with it — deleting something
 * the user is being asked to answer, to tidy punctuation. So the sentence that
 * stands on its own is kept and only the fragment after it goes.
 */
function trimTrailingLeadIn(line: string): string {
  if (!/:\s*$/.test(line) || isListItem(line)) return line;
  // A complete sentence, then whitespace, then the lead-in fragment ending in ':'.
  const withSentence = /^([\s\S]*[.!?])\s+\S[\s\S]*:\s*$/.exec(line);
  return withSentence ? withSentence[1] : '';
}

function dropOrphanedLeadIns(text: string): string {
  const lines = text.split('\n');

  lines.forEach((line, i) => {
    if (!line.includes(REMOVED)) return;
    const before = line.slice(0, line.indexOf(REMOVED));
    if (before.trim() !== '') {
      // The block sat at the end of this same line ("Sure: {json}").
      lines[i] = trimTrailingLeadIn(before) + line.slice(line.indexOf(REMOVED));
      return;
    }
    // The block was on its own line — the lead-in is the previous prose line.
    // ⚠️ Walk back past blank lines and earlier removals: consecutive suggestion
    // blocks are common, and stopping at the first blank would miss the sentence.
    let j = i - 1;
    while (j >= 0 && (lines[j].trim() === '' || lines[j].includes(REMOVED))) j -= 1;
    if (j >= 0) lines[j] = trimTrailingLeadIn(lines[j]);
  });

  return lines.map((l) => l.split(REMOVED).join('')).join('\n');
}

export function stripSuggestionMarkup(content: string): string {
  let out = content;

  // Remove fenced code blocks (```json … ``` or any ``` … ```).
  out = out.replace(/```[\s\S]*?```/g, REMOVED);

  // Remove a leftover bare top-level JSON object if the model emitted one without
  // a fence — only when the whole span actually parses (so prose with stray
  // braces is left alone).
  out = out.replace(/\{[\s\S]*\}/g, (m) => {
    try {
      JSON.parse(m);
      return REMOVED;
    } catch {
      return m;
    }
  });

  out = dropOrphanedLeadIns(out);

  // Tidy whitespace left behind by the removals.
  return out.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * The text to render for a message bubble. Assistant messages get their
 * suggestion markup stripped; if that leaves nothing (the reply was only the
 * JSON), fall back to a friendly line so the bubble is never blank.
 */
export function messageDisplayText(content: string, isUser: boolean): string {
  if (isUser) return content;
  const cleaned = stripSuggestionMarkup(content);
  return cleaned || "Here's a suggestion you can apply below. ✨";
}
